import React from 'react';

import {
  Button,
  CircleButton,
  DesktopStepIndicator,
  HomeIconButton,
  MovingStepIndicator,
  ScreenContainer,
  ScreenHeader,
  StepIndicatorBackground,
  StepIndicatorLabels,
} from 'ui/component';
import { StyleUtil } from 'ui/util';
import { ContentCardSlider, JourneyFooter, JourneyHighlightedCircleButton, JourneyInfoDialog } from '../../component';
import { useNavigationContext, useStateContext } from '../../context';
import { useEricssonGTM, useStepperConfig } from '../../hook';
import classNames from './EABDJourneyScreen.module.scss';

export const JourneyScreen = React.memo(() => {
  const { push, reset } = useNavigationContext();
  const { shouldShowJourneyInfoDialogAtFirst, setShouldShowJourneyInfoDialogAtFirst } = useStateContext();
  const ericssonGTM = useEricssonGTM();
  const { isMobile, numberOfItemsPerPage, contentCards } = useStepperConfig();
  const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(0);

  const AfterSliderScrollCallback = React.useCallback((index: number) => setCurrentPageIndex(index), []);
  const OnHomeButtonPressCallback = React.useCallback(() => reset(), [reset]);
  const OnKeyToSuccessButtonPressCallback = React.useCallback(() => push('key_to_success'), [push]);

  const [isOverlayVisible, setOverlayVisible] = React.useState<boolean>(false);

  // Effect for showing the info dialog
  React.useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (!isMobile && shouldShowJourneyInfoDialogAtFirst && currentPageIndex === 0) {
      const handler = () => {
        setOverlayVisible(true);
        setShouldShowJourneyInfoDialogAtFirst(false);
      };

      timeout = setTimeout(handler, 250);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isMobile, shouldShowJourneyInfoDialogAtFirst, currentPageIndex, setShouldShowJourneyInfoDialogAtFirst]);

  // Effect for hiding the info dialog when it was visible
  // and we resize the browser window
  React.useEffect(() => {
    if (isMobile && isOverlayVisible) {
      setOverlayVisible(false);
    }
  }, [isMobile, isOverlayVisible]);

  React.useEffect(() => {
    const currentStep = currentPageIndex + 1;
    ericssonGTM.onNewJourneyStep(currentStep);
  }, [currentPageIndex, ericssonGTM]);

  const GetLabelForCardIndexCallback = React.useCallback((cardIndex: number) => {
    switch (cardIndex) {
      case 1:
        return 'Automation of operations &\noptimization';
      case 2:
        return 'Boosting automation\ncapabilties with AI';
      case 3:
        return 'Moving towards intent-based telecom networks';
      case 4:
        return 'Realizing the vision of\ncognitive & autonomous\ntelecom networks';
      default:
        return '';
    }
  }, []);

  const GetDesktopStepIndicatorLabelForIndexCallback = React.useCallback((index: number) => {
    switch (index) {
      case 0:
        return 'Scripting';
      case 1:
        return 'Process Automation';
      case 2:
        return 'Machine Learning';
      case 3:
        return 'Machine Reasoning';
      case 4:
        return 'Zero-touch';
      default:
        return '';
    }
  }, []);

  const GetDesktopStepIndicatorDescriptionForIndexCallback = React.useCallback((index: number) => {
    switch (index) {
      case 0:
        return 'Automation of operations &\noptimization';
      case 1:
        return 'Boosting automation capabilities\nwith AI';
      case 2:
        return 'Moving towards intent-based\ntelecom networks';
      case 3:
        return 'Realizing the vision of cognitive &\nautonomous telecom networks';
      default:
        return '';
    }
  }, []);

  const OnInfoDialogClosePressCallback = React.useCallback(() => setOverlayVisible(false), []);

  const NumberOfNotEmptyContentCards = React.useMemo(() => contentCards.filter((contentCard) => contentCard !== 'empty').length, [
    contentCards,
  ]);

  const KeyToSuccessMobileElement = React.useMemo(() => {
    if (!isMobile) {
      return null;
    }

    const keyToSuccessContainerClassNames: string[] = [classNames.keyToSuccessMobileContainer, classNames.invisible];

    const isVisible = currentPageIndex === contentCards.length - 1;
    if (isVisible) {
      keyToSuccessContainerClassNames.push(classNames.visible);
    }

    return (
      <div className={StyleUtil.mergeClassNames(keyToSuccessContainerClassNames)}>
        <p className={classNames.buttonText}>{'See the key to our success'}</p>
        <CircleButton arrowDirection={'right'} onPress={OnKeyToSuccessButtonPressCallback} />
      </div>
    );
  }, [isMobile, contentCards, currentPageIndex, OnKeyToSuccessButtonPressCallback]);

  const KeyToSuccessDesktopElement = React.useMemo(() => {
    if (isMobile) {
      return null;
    }

    const keyToSuccessContainerClassNames: string[] = [classNames.keyToSuccessDesktopContainer, classNames.invisible];

    const isVisible = currentPageIndex === contentCards.length - 3;
    if (isVisible) {
      keyToSuccessContainerClassNames.push(classNames.visible);
    }

    return (
      <div className={StyleUtil.mergeClassNames(keyToSuccessContainerClassNames)}>
        <p className={classNames.buttonText}>What is the key to our success in our journey?</p>
        <Button onPress={OnKeyToSuccessButtonPressCallback}>See more</Button>
      </div>
    );
  }, [isMobile, contentCards, currentPageIndex, OnKeyToSuccessButtonPressCallback]);

  const OverlayElement = React.useMemo(() => {
    const overlayClassNames: string[] = [classNames.overlay];

    if (isOverlayVisible) {
      overlayClassNames.push(classNames.visible);
    }

    return (
      <div onClick={OnInfoDialogClosePressCallback} className={StyleUtil.mergeClassNames(overlayClassNames)}>
        <JourneyHighlightedCircleButton isVisible={isOverlayVisible} arrowDirection={'left'} />
        <JourneyInfoDialog isVisible={isOverlayVisible} onClosePress={OnInfoDialogClosePressCallback} />
        <JourneyHighlightedCircleButton isVisible={isOverlayVisible} arrowDirection={'right'} />
      </div>
    );
  }, [isOverlayVisible, OnInfoDialogClosePressCallback]);

  return (
    <ScreenContainer shouldUseAppHorizontalMargin={false}>
      <div className={classNames.mobileHeader}>
        <div className={classNames.mobileHeaderContent}>
          <div className={classNames.mobileHomeIconButtonWrapper}>
            <HomeIconButton onPress={OnHomeButtonPressCallback} />
          </div>
          <div className={classNames.mobileStepIndicatorWrapper}>
            <MovingStepIndicator currentStepIndex={currentPageIndex} />
            <StepIndicatorBackground
              numberOfSteps={5}
              numberOfMergedNodes={7}
              currentStepIndex={currentPageIndex}
              style={{ marginTop: '-40px' }}
            />
            <StepIndicatorLabels
              currentStepIndex={currentPageIndex}
              getLabelForStepIndex={GetLabelForCardIndexCallback}
              style={{ marginTop: '-4px' }}
            />
          </div>
        </div>
      </div>
      <div className={classNames.desktopHeader}>
        <ScreenHeader shouldShowHome={true} onHomePress={OnHomeButtonPressCallback}>
          <div className={classNames.stepIndicatorWrapper}>
            <DesktopStepIndicator
              numberOfItems={NumberOfNotEmptyContentCards}
              currentIndex={currentPageIndex}
              getLabelForIndex={GetDesktopStepIndicatorLabelForIndexCallback}
              getDescriptionForIndex={GetDesktopStepIndicatorDescriptionForIndexCallback}
            />
          </div>
        </ScreenHeader>
      </div>
      <div className={classNames.content}>
        <div className={classNames.sliderWrapper}>
          <ContentCardSlider
            contentCards={contentCards}
            currentIndex={currentPageIndex}
            initialPageIndex={currentPageIndex}
            isMobile={isMobile}
            afterScroll={AfterSliderScrollCallback}
          />
        </div>
      </div>
      <footer className={classNames.footer}>
        <JourneyFooter
          leftTitle={'Reactive'}
          leftSubtitle={'Incident-driven'}
          rightTitle={'proactive'}
          rightSubtitle={'Data-driven'}
          numberOfItems={contentCards.length}
          numberOfItemsPerPage={numberOfItemsPerPage}
          currentItemIndex={currentPageIndex}
        />
      </footer>
      {KeyToSuccessMobileElement}
      {KeyToSuccessDesktopElement}
      {OverlayElement}
    </ScreenContainer>
  );
});
